/**
 * Hook centralisant la logique notifications :
 * queries, mutations, realtime, navigation
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/store/useAppStore';
import { Permission } from '@/types';
import { hasUserPermission } from '@/lib/permissions';
import {
  getUnreadNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '@/services/notification.service';
import type { Notification } from '@/services/notification.service';

export function useNotifications() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  // Query
  const { data: notifications = [], isLoading } = useQuery<Notification[]>({
    queryKey: ['unread-notifications'],
    queryFn: getUnreadNotifications,
  });

  // Realtime subscription
  useEffect(() => {
    if (!profile?.id) return;

    const channel = supabase
      .channel('notifications-changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${profile.id}`,
      }, (payload) => {
        const newNotif = payload.new as {
          type?: string;
          context?: {
            content_title?: string;
            invoice_number?: string;
            provider_name?: string;
            is_linked?: boolean;
          };
        };
        const context = newNotif.context || {};

        if (newNotif.type === 'comment') {
          toast.success(`Nouveau commentaire sur ${context.content_title || 'un contenu'}`, { icon: '💬', duration: 5000 });
        } else if (newNotif.type === 'invoice') {
          toast.success(`Nouvelle facture : ${context.invoice_number || ''}`, { icon: '💰', duration: 6000 });
        } else if (newNotif.type === 'status_change') {
          toast.success(`Statut mis à jour : ${context.content_title || ''}`, { icon: '🚀', duration: 4000 });
        } else if (newNotif.type === 'provider_pending_validation') {
          toast.success(`Nouveau prestataire à valider : ${context.provider_name || ''}`, { icon: '🆕', duration: 6000 });
        } else if (newNotif.type === 'invoice_uploaded') {
          toast.success(`Nouvelle facture de ${context.provider_name || ''} (${context.is_linked ? 'rattachée' : 'non rattachée'})`, { icon: '📄', duration: 6000 });
        }

        queryClient.invalidateQueries({ queryKey: ['unread-notifications'] });
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${profile.id}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['unread-notifications'] });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [profile?.id, queryClient]);

  // Mutations
  const markAsReadMutation = useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['unread-notifications'] }); },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['unread-notifications'] }); },
  });

  // Navigation
  // Variables locales pour que les deps du useCallback correspondent exactement aux valeurs utilisées
  const profileRole = profile?.role ?? null;
  const profilePermissions = profile?.permissions;

  const getNotificationRoute = useCallback((notification: Notification): string => {
    switch (notification.type) {
      case 'comment':
      case 'status_change':
        if ('content_id' in notification && notification.content_id) {
          return `/editorial-calendar?content_id=${notification.content_id}`;
        }
        return '/editorial-calendar';
      case 'invoice':
        if (profileRole && hasUserPermission(profileRole, profilePermissions, Permission.VALIDATE_INVOICES)) {
          return '/invoices';
        }
        return '/my-invoices';
      case 'provider_pending_validation':
        return '/users';
      case 'invoice_uploaded':
        if (profileRole && hasUserPermission(profileRole, profilePermissions, Permission.VALIDATE_INVOICES)) {
          return '/invoices';
        }
        return '/my-invoices';
      default:
        return '/';
    }
  }, [profileRole, profilePermissions]);

  const handleNotificationClick = useCallback((notification: Notification) => {
    markAsReadMutation.mutate(notification.id);
    navigate(getNotificationRoute(notification));
    setIsOpen(false);
  }, [markAsReadMutation, navigate, getNotificationRoute]);

  return {
    isOpen,
    setIsOpen,
    notifications,
    isLoading,
    unreadCount: notifications.length,
    markAsReadMutation,
    markAllAsReadMutation,
    handleNotificationClick,
    handleMarkAllAsRead: () => markAllAsReadMutation.mutate(),
  };
}

// Helpers
export function formatNotificationTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).format(date);
}
